import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { HeaderMiniDropdown, MarketViewContainer } from "../pages/mindSHARE";
import { MARKET_FAVORITES, matchesMarketFavorite } from "../services/mindshareMarket";
import { layoutRect, viewportHeight } from "../utils/displayDensity";
import "./marketMind.css";

export default function MarketMindPanel({ label, rows, filtered, splitView, onSplitChange, hasActiveFilters,
  selectionIds = [], shape, onShapeChange, favorites, onFavoritesChange, loading, controls, marketProps,
  opportunityMapRows, opportunityMapRenderer, favoriteOptions = MARKET_FAVORITES, showToolbar = true,
  centeredCanvas = false }) {
  const sectionRef = useRef(null);
  const hostRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0, availableHeight: 0 });
  useLayoutEffect(() => {
    const host = hostRef.current;
    const section = sectionRef.current;
    const measure = () => {
      const top = layoutRect(centeredCanvas ? section : host).top;
      const availableHeight = Math.max(300, viewportHeight() - top - 8);
      const height = centeredCanvas ? Math.max(270, availableHeight * 0.96) : availableHeight;
      const next = { width: host.clientWidth, height, availableHeight };
      setSize(previous => previous.width === next.width && previous.height === next.height &&
        previous.availableHeight === next.availableHeight ? previous : next);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(host);
    observer.observe(section);
    window.addEventListener("resize", measure);
    return () => { observer.disconnect(); window.removeEventListener("resize", measure); };
  }, [centeredCanvas]);
  const selectedRows = useMemo(() => {
    const byId = new Map(filtered.flatMap(game => [
      game.selectionKey,
      game.id,
      game.gameKey,
      game.name,
    ].filter(Boolean).map(id => [id, game])));
    return rows.filter(game => byId.has(game.id)).map(game => {
      const selected = byId.get(game.id);
      return Number.isFinite(selected.mindshare) ? { ...game, ...selected } : game;
    });
  }, [rows, filtered]);
  const selectionSet = useMemo(() => new Set(selectionIds), [selectionIds]);
  const comparisonRows = useMemo(
    () => rows.filter(game => selectionSet.has(game.id)),
    [rows, selectionSet]
  );
  const splitFiltered = useMemo(() => {
    const filteredById = new Map((hasActiveFilters ? selectedRows : []).map(game => [game.id, game]));
    return rows.filter(game => filteredById.has(game.id) || selectionSet.has(game.id))
      .map(game => filteredById.get(game.id) || game);
  }, [rows, selectedRows, selectionSet, hasActiveFilters]);
  const favoritePreview = useMemo(() => favorites.map(value => ({ type: "favorite", value })), [favorites]);
  const preview = useMemo(() => {
    if (!hasActiveFilters && !selectionSet.size) return favoritePreview;
    const filteredIds = hasActiveFilters
      ? selectedRows
        .filter(game => !favorites.length || favorites.some(value => matchesMarketFavorite(game, value)))
        .map(game => game.id)
      : rows.filter(game => favorites.some(value => matchesMarketFavorite(game, value))).map(game => game.id);
    return { type: "ids", value: new Set([...filteredIds, ...comparisonRows.map(game => game.id)]) };
  }, [hasActiveFilters, selectedRows, rows, favorites, favoritePreview, selectionSet, comparisonRows]);
  const hasMarketSubset = hasActiveFilters || selectionSet.size > 0;
  const splitEnabled = splitView && hasMarketSubset;
  const splitLabel = hasActiveFilters && selectionSet.size
    ? "Filters + selected"
    : hasActiveFilters ? "Current filters" : "Selected games";
  const opportunityRows = splitEnabled
    ? splitFiltered
    : hasActiveFilters ? selectedRows : rows;
  const mapRows = opportunityMapRows || rows;
  const mapVisibleRows = useMemo(() => {
    if (!opportunityMapRows) return opportunityRows;
    const keyOf = game => game.id || game.selectionKey || game.gameKey || game.name;
    const dashboardIds = new Set(filtered.flatMap(game =>
      [game.selectionKey, game.id, game.gameKey, game.name].filter(Boolean)));
    const filteredRows = hasActiveFilters
      ? mapRows.filter(game => dashboardIds.has(keyOf(game)))
      : [];
    if (!splitEnabled) return hasActiveFilters ? filteredRows : mapRows;
    const included = new Set([...filteredRows, ...mapRows.filter(game => selectionSet.has(keyOf(game)))]);
    return mapRows.filter(game => included.has(game));
  }, [opportunityMapRows, opportunityRows, mapRows, filtered, hasActiveFilters, splitEnabled, selectionSet]);
  const layoutOptions = [["bubbles", "Circles"], ["squares", "Squares"]];
  if (opportunityMapRenderer) layoutOptions.push(["opportunity", "Opportunity Map"]);
  return <section ref={sectionRef} className="mg-market-shared" aria-label={label}>
    {showToolbar && <div className="mm-controls-row">
      <div className="mm-controls-group">
        {controls}
        <HeaderMiniDropdown multi placeholder="Highlight Favorites" value={favorites} onChange={onFavoritesChange}
          options={favoriteOptions} active={favorites.length > 0} width={190} />
      </div>
      <div className="mm-layout-comparison" role="group" aria-label="Market Mind layout">
        {layoutOptions.map(([value, title]) =>
          <button key={value} type="button" aria-pressed={shape === value} onClick={() => onShapeChange(value)}>{title}</button>)}
      </div>
      {hasMarketSubset && <button type="button" className="mm-split-toggle" aria-pressed={splitEnabled}
        onClick={() => onSplitChange(!splitEnabled)}>{splitEnabled ? "Unsplit" : "Split"}</button>}
    </div>}
    <div className="mg-market-canvas-stage" style={centeredCanvas ? {
      height: size.availableHeight || "calc(100vh - 200px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    } : undefined}>
      <div ref={hostRef} className="mg-market-canvas" style={{ width: "100%", height: size.height || 600 }}>
        {size.width > 0 && shape === "opportunity" && opportunityMapRenderer
          ? opportunityMapRenderer({
            width: size.width,
            height: size.height,
            rows: mapVisibleRows,
            populationRows: rows,
            selectionIds: selectionSet,
            favorites,
          })
          : size.width > 0 && <MarketViewContainer filtered={rows} preview={preview} width={size.width} height={size.height}
          displayMode={shape} splitEnabled={splitEnabled} splitFiltered={splitFiltered} splitPreview={favoritePreview} fitSplitToPanel
          splitLabel={splitLabel} isEmpty={!rows.length} loading={loading} marketProps={marketProps} />}
      </div>
    </div>
  </section>;
}
